import type paper from "paper";
import type { ConstraintRef, ConstructionLine, Project } from "../Project";
import type { EditorContext } from "../../tools/EditorContext";
import type { Scene } from "../../canvas/Scene";

/** Issue #180 follow-up: a `Connection` represents one kind of
 *  relationship between construction lines. Three live today —
 *  coincident-snap, offset, and mirror — and each used to ship its
 *  own near-identical implementations of chip rendering, Properties
 *  row rendering, live re-derivation, commit-pipeline pass, drag
 *  closure walking, and pruning. Folding them into one `Connection`
 *  interface means adding a fourth kind (translate, scale, rotate,
 *  …) is one new file + one registration line. */

/** A chip rendered in the layers-panel chip strip. The same shape
 *  also drives the Properties-panel Connections rows (see
 *  `ConnectionRowSpec`), which lets the panels share a single
 *  rendering loop instead of three per-kind ones. */
export interface ConnectionChipSpec {
  /** Connection kind for sort order + the CSS modifier class
   *  (`offset-chip`, `mirror-chip`, etc.). */
  kind: ConnectionKindId;
  /** Stable identifier shared across EVERY chip + Properties row
   *  participating in the SAME logical relationship. The DOM
   *  `data-connection-key` carries this verbatim so cross-highlight
   *  selectors can find peers across the panel in one
   *  `querySelectorAll` call. Snap uses the constraint id; offset
   *  uses `${sourceId}->${derivedId}`; mirror uses
   *  `mirror:${derivedId}` so the three chips for one mirror
   *  (source + axis + derived) all share it. */
  connectionKey: string;
  /** Role within the relationship — `"derived"`, `"source"`,
   *  `"axis"` for offset / mirror; constraint kind name for snap
   *  (`"coincident"`). Drives the row label and the hover dataset
   *  attribute. */
  role: string;
  /** Raw `<svg>` HTML for the chip glyph. The chip's CSS sets
   *  `width: 16px` and `currentColor` so the icon picks up the
   *  surrounding accent. */
  iconSvg: string;
  /** ARIA label for the chip (and the matching Properties row). */
  ariaLabel: string;
  /** What a CLICK on the chip / row selects — the OTHER side of the
   *  relationship (#221): the partner line, the traced piece, the
   *  matched seam. The parts panel then auto-scrolls the selection
   *  into view (#868). Omitted → click stays a no-op (e.g. a
   *  one-to-many chip with no single partner). */
  clickTarget?: {
    constructionId?: string;
    pieceId?: string;
    stitchId?: string;
    /** One of a part's cutouts (#1076) — the far side of a note pointing at a
     *  cutout rather than at the part's own outline. */
    cutoutId?: string;
    /** A written note (#157) — the far side of a note's "points at" link. */
    noteId?: string;
  };
  /** `title=` attribute for the chip / row — same text the user
   *  sees on hover. */
  title: string;
  /** Extra CSS class beyond `constraint-chip`. Today: `offset-chip`,
   *  `mirror-chip`. (Snap chips don't add a modifier class — the
   *  base `.constraint-chip` is enough.) */
  className?: string;
  /** Drives the broken-state CSS for coincident chips whose
   *  anchor pair has drifted apart. Other kinds always set false
   *  because their geometry re-derives exactly on every commit. */
  unsatisfied?: boolean;
  /** What canvas overlay the chip highlights on hover. Passed
   *  verbatim to `Scene.setConstructionConstraintHover({ refs })`.
   *  For whole-line halos use `[{ kind: "line", constructionId }]`;
   *  for an anchor ring use the anchor ref directly. */
  hoverRefs: ConstraintRef[];
  /** Per-kind dataset entries set on the chip / row element so the
   *  cross-highlight peer query can find them (`data-connection-kind`
   *  + `data-connection-key` are added automatically; this is for
   *  legacy keys that some specs may still query). */
  extraDatasetEntries?: ReadonlyArray<[string, string]>;
}

/** A row rendered in the Properties → Connections section. Carries
 *  everything the chip carries plus a human-readable label and the
 *  detach commit. */
export interface ConnectionRowSpec extends ConnectionChipSpec {
  /** Plain-language description shown next to the icon, e.g.
   *  "Mirror of Rectangle 2", "Offset → Pill 1", "Coincident with
   *  anchor". */
  label: string;
  /** Drops the relationship. For offset/mirror: removes the
   *  back-ref field from the derived line (last-derived geometry
   *  stays). For coincident: removes the constraint record from
   *  `project.constraints`. Omitted for emergent relationships that
   *  can't be detached directly (e.g. the construction-stitch grid,
   *  #199) — the row then renders read-only, with no × button. */
  onDetach?: (ctx: EditorContext) => void;
  /** Optional "Apply" affordance — surfaced only for unsatisfied
   *  coincident constraints today. Forces the constraint's
   *  partners back onto the first ref's position. */
  onApply?: (ctx: EditorContext) => void;
}

/** Args for the commit-pipeline `derive` pass. Each connection's
 *  `derive` reads + returns the whole project; the registry
 *  composes them in `order`. Returns input project unchanged
 *  when nothing changed (cheap no-op contract that lets callers
 *  skip re-snapshotting). */
export interface ConnectionDeriveArgs {
  project: Project;
  scope: paper.PaperScope;
}

/** Args for `pruneForDeletedLines`. */
export interface ConnectionPruneArgs {
  project: Project;
  deletedLineIds: ReadonlySet<string>;
}

/** Args for per-frame live re-derive (called from drag handlers
 *  inside `Scene.rederiveConnectionsAffectedBy`). `changedId` is
 *  the construction line whose RENDERED geometry just shifted in
 *  the scene; the connection re-derives every dependent rendered
 *  item from that live geometry. */
export interface ConnectionLiveRederiveArgs {
  scene: Scene;
  changedId: string;
}

/** Args for the forward-closure walker. */
export interface ConnectionClosureArgs {
  project: Project;
  originIds: ReadonlyArray<string>;
}

export type ConnectionKindId =
  | "snap"
  | "offset"
  | "mirror"
  | "trace"
  // Stitch-line matching (#199). Not a construction-line connection (it has no
  // entry in the `CONNECTIONS` registry), but it reuses the chip / Connections-
  // row UI so a matched seam reads like any other relationship.
  | "stitchMatch"
  // Construction-stitch grid (#199 Phase 3): seams tracing the same construction
  // line auto-align to a shared hole grid. Emergent (no record, read-only row) —
  // also reuses the chip / Connections-row UI to show which seam leads the grid.
  | "stitchGrid"
  // A written note's callout (#157) — note ↔ the part it points at. Also
  // registry-less; see `noteConnection.ts` for why.
  | "note";

export interface Connection {
  kind: ConnectionKindId;
  /** Sort order across all three kinds — controls chip/row display
   *  order in the panel AND the commit-pipeline `derive` order.
   *  Snap = 0 (no derive, just pruning); offset = 1 (must run
   *  before mirror because a mirror-of-offset reads the offset's
   *  freshly-derived pathData); mirror = 2. */
  order: number;
  /** Chips this connection contributes to one construction line's
   *  layers-panel row. Returns empty when the line doesn't
   *  participate in any relationship of this kind. */
  chipsForLine(
    line: ConstructionLine,
    project: Project,
    ctx: {
      allLines: ReadonlyArray<ConstructionLine>;
      brokenIds?: ReadonlySet<string>;
    },
  ): ConnectionChipSpec[];
  /** Properties-panel rows for one construction line. Same shape
   *  as chips plus a label + detach commit. */
  rowsForLine(
    line: ConstructionLine,
    project: Project,
    ctx: {
      allLines: ReadonlyArray<ConstructionLine>;
      brokenIds?: ReadonlySet<string>;
    },
  ): ConnectionRowSpec[];
  /** Commit-pipeline pass. Optional — snap has no commit-time
   *  derive (constraint enforcement runs per-drag). Returns input
   *  unchanged on no-op. */
  derive?(args: ConnectionDeriveArgs): Project;
  /** Drop project state when construction lines are deleted.
   *  Called from delete handlers (layers panel × button,
   *  Backspace, etc.) BEFORE the deletion commit lands. Snap drops
   *  constraints touching any deleted line; offset/mirror drop the
   *  derived line itself. */
  pruneForDeletedLines(args: ConnectionPruneArgs): Project;
  /** Per-frame scene re-derive of dependents whose RENDERED
   *  geometry depends on the changed line. Snap has no live
   *  rederive (chain translation runs through `translateDragClosure`
   *  in the drag handler). Offset/mirror update their derived items
   *  in place. */
  rederiveLive?(args: ConnectionLiveRederiveArgs): void;
  /** Forward closure: every line whose geometry follows the
   *  origins via this kind's adjacencies. Excludes the origins
   *  themselves so callers can union cheaply. Snap: undirected
   *  snap-edge walk; offset/mirror: derived lines that ultimately
   *  reference the origins. */
  forwardClosure(args: ConnectionClosureArgs): Set<string>;
  /** Walk up the derivation chain to the non-derived root. Returns
   *  the input id when the line is already a root (or when this
   *  kind has no concept of "root" — snap returns input id). */
  resolveRoot(project: Project, lineId: string): string;
  /** True when `line` is a derived line of this kind. Snap always
   *  returns false (no anchor is "derived" — coincident pairs are
   *  equals). Offset/mirror return true when their back-ref field
   *  is set. */
  isDerived(line: ConstructionLine): boolean;
  /** Cycle detection at commit time. Returns the set of derived
   *  line ids whose source chain re-enters themselves. Snap: empty
   *  (no chain). Offset/mirror walk their back-ref. The registry
   *  strips the back-ref on cycled lines before running `derive`
   *  so the deriver can't loop. */
  detectCycles?(project: Project): Set<string>;
  /** Strip the back-ref(s) for one line (used by the registry to
   *  detach cycled lines without losing the rendered geometry).
   *  Snap: drops constraints touching the line. Offset/mirror:
   *  drops `offsetSource` / `mirrorSource`. */
  stripBackRefs?(project: Project, lineIds: ReadonlySet<string>): Project;
}

/** The registered connections in display + derive order. Mutable
 *  at module load so test harnesses can swap in stubs, but treated
 *  as readonly at runtime. Populated by side-effect imports in
 *  `./index.ts` to break the import cycle (each kind file would
 *  otherwise depend on Connection.ts and vice versa). */
export const CONNECTIONS: Connection[] = [];

/** Register a connection kind. Called by each kind's module at
 *  load time. Sorted by `order` after each registration so the
 *  pipeline always sees a stable iteration order. */
export function registerConnection(conn: Connection): void {
  // De-dup by kind so HMR reloads (and test stubs) don't end up
  // with two snap entries fighting over the pipeline.
  const idx = CONNECTIONS.findIndex((c) => c.kind === conn.kind);
  if (idx >= 0) {
    CONNECTIONS[idx] = conn;
  } else {
    CONNECTIONS.push(conn);
  }
  CONNECTIONS.sort((a, b) => a.order - b.order);
}

/** Commit-pipeline pass: run every connection's `derive` in order,
 *  with `detectCycles` first per kind so a cycle-strip lands in
 *  the same commit snapshot. Snap has no `derive` (no-op). */
export function deriveAllConnections(
  project: Project,
  scope: paper.PaperScope,
): Project {
  // Run the registry in registration order, then loop until the
  // project state is stable. Cross-kind dependencies need this:
  // an offset whose source is a mirror (issue #180 redirect — the
  // auto-created `offset(M, distance)` sibling) needs the mirror
  // to be derived BEFORE the offset's pathData is computed. Single-
  // pass execution in [snap, offset, mirror] order leaves such
  // offsets reading stale mirror geometry until the next commit.
  // Iteration until stable handles arbitrary cross-kind ordering
  // without per-connection topological wiring.
  let current = project;
  for (let iter = 0; iter < 8; iter++) {
    let next = current;
    for (const conn of CONNECTIONS) {
      if (conn.detectCycles && conn.stripBackRefs) {
        const cycled = conn.detectCycles(next);
        if (cycled.size > 0) {
          next = conn.stripBackRefs(next, cycled);
        }
      }
      if (conn.derive) {
        next = conn.derive({ project: next, scope });
      }
    }
    if (next === current) return current;
    current = next;
  }
  return current;
}

/** Run every connection's `pruneForDeletedLines` in registration
 *  order so a snap-on-mirror is dropped by snap, then the mirror
 *  itself is dropped by mirror. Returns the input project
 *  unchanged when no kind needed to prune. */
export function pruneAllConnections(args: ConnectionPruneArgs): Project {
  let next = args.project;
  for (const conn of CONNECTIONS) {
    next = conn.pruneForDeletedLines({
      project: next,
      deletedLineIds: args.deletedLineIds,
    });
  }
  return next;
}

/** Interleaved BFS across every connection's `forwardClosure`
 *  adjacencies. Today snap is undirected; offset is undirected
 *  (translation preserves the relationship); mirror is undirected
 *  AND has TWO drivers (source + axis) so a closure starting at
 *  the source walks via the derived to the axis (and vice versa).
 *
 *  The walk is single-pass — each kind contributes its
 *  neighbours-of-each-frontier-node as the BFS grows, so a snap
 *  edge can bridge into an offset edge into a mirror edge mid-
 *  walk. This mirrors what `dragClosure.computeDragClosure` used
 *  to do by hand for just snap + offset. */
export function forwardClosureAll(
  project: Project,
  originIds: ReadonlyArray<string>,
): Set<string> {
  const out = new Set<string>();
  if (originIds.length === 0) return out;
  const queue: string[] = [...new Set(originIds)];
  for (const id of queue) out.add(id);
  while (queue.length > 0) {
    const cur = queue.shift()!;
    for (const conn of CONNECTIONS) {
      const neighbours = conn.forwardClosure({ project, originIds: [cur] });
      for (const n of neighbours) {
        if (out.has(n)) continue;
        out.add(n);
        queue.push(n);
      }
    }
  }
  // Strip the original seeds — callers want partners only. (Match
  // the historic `reachableOffsets` contract so we don't break
  // call sites that union-then-subtract origins.)
  for (const id of originIds) out.delete(id);
  return out;
}

/** Per-frame scene re-derive: ask every kind to refresh its
 *  rendered dependents of `changedId`. Snap is a no-op (its
 *  enforcement runs through `translateDragClosure` in the drag
 *  handler, not Scene). Offset before mirror so a mirror of an
 *  offset reads the just-updated rendered offset pathData. */
export function liveRederiveAllAffectedBy(
  scene: Scene,
  changedId: string,
): void {
  for (const conn of CONNECTIONS) {
    conn.rederiveLive?.({ scene, changedId });
  }
}

/** Compose every kind's `resolveRoot` until the id stops changing
 *  (a fixpoint walk). Used by line-drag to redirect a derived-line
 *  grab to the root so the rigid translation carries every
 *  transitive descendant through the same scene path. */
export function resolveRootAllConnections(
  project: Project,
  lineId: string,
): string {
  let cur = lineId;
  for (let i = 0; i < 1000; i++) {
    let nextId = cur;
    for (const conn of CONNECTIONS) {
      nextId = conn.resolveRoot(project, nextId);
    }
    if (nextId === cur) return cur;
    cur = nextId;
  }
  return cur;
}

/** True when ANY registered connection considers the line a
 *  derived line. Used by edit handlers that need to know "is this
 *  line's geometry owned by another line?" regardless of kind.
 *  (Per-kind gates are still available on each `Connection`
 *  instance for handlers that need to allow/refuse by kind.) */
export function isDerivedByAnyConnection(line: ConstructionLine): boolean {
  for (const conn of CONNECTIONS) {
    if (conn.isDerived(line)) return true;
  }
  return false;
}
